import { useCallback, useRef, useState } from "react";

const RECENTS_KEY = "global-search:recents";
const RECENTS_MAX = 5;

function readRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed)
      ? parsed.filter((p): p is string => typeof p === "string")
      : [];
  } catch {
    return [];
  }
}

function writeRecents(paths: string[]): void {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(paths.slice(0, RECENTS_MAX)));
  } catch {
    /* storage unavailable — recents are best-effort */
  }
}

export const useRecents = () => {
  const [paths, setPaths] = useState<string[]>(readRecents);
  // Ref keeps latest paths available in callbacks without stale-closure risk.
  const pathsRef = useRef(paths);
  pathsRef.current = paths;

  const add = useCallback((path: string) => {
    const next = [path, ...pathsRef.current.filter((p) => p !== path)].slice(0, RECENTS_MAX);
    setPaths(next);
    writeRecents(next);
  }, []);

  const remove = useCallback((path: string) => {
    const next = pathsRef.current.filter((p) => p !== path);
    setPaths(next);
    writeRecents(next);
  }, []);

  const clear = useCallback(() => {
    setPaths([]);
    writeRecents([]);
  }, []);

  return { paths, add, remove, clear } as const;
};
